/**
 * Build Your Own Routine (BYOR)
 *
 * Mirrors BYOB's pattern but lives as its own experience.
 *
 * Layout (from byor.liquid):
 *  - section.blocks = N selection steps. The review page is rendered
 *    as an ADDITIONAL group appended after the blocks loop, so the DOM
 *    has N + 1 routine-group elements and N + 1 nav items.
 *  - Each selection group has a grid of products. Each product is either
 *    a single-variant button (data-routine-select) or a multi-variant
 *    quickview trigger that opens byor_quickview in a modal.
 *  - The review group contains [data-routine-selected] (filled by JS),
 *    [data-routine-gifts] (server-rendered from settings.gifts), and
 *    [data-routine-submit] (the add-to-cart trigger).
 *
 * Cart line-item properties added to every routine item:
 *   _gid          - random int shared by every item in this routine
 *   _routineType  - section.settings.routine_type
 *   _routineSize  - total number of items (selections + gifts)
 *   _routineGift  - 'TRUE' on gift lines only
 */

const BYOR_EVENTS = {
  productSelected: 'byor:product-selected',
};

// Matches the hardcoded discount in byor.liquid: `100 | minus: 30`.
// Selected products in the routine are sold at 70% of variant.price.
const BYOR_DISCOUNT_MULTIPLIER = 0.7;

class BuildYourRoutine extends HTMLElement {
  constructor() {
    super();

    this.routineType = this.dataset.routineType || '';
    // data-total-steps is section.blocks.size — the number of selection steps.
    // The review page is appended as an extra group at index = selectionCount.
    this.totalSteps = parseInt(this.dataset.totalSteps, 10) || 0;
    this.selectionCount = this.totalSteps;

    // bundle[i] holds the user's selection for step i, or null.
    this.bundle = new Array(this.selectionCount).fill(null);
    this.currentStep = 0;

    this.groups = Array.from(this.querySelectorAll('.routine-group'));
    this.navigationItems = Array.from(this.querySelectorAll('.routine-navigation__item'));
    this.reviewGroup = this.querySelector('.routine-group--review');
    this.selectedContainer = this.querySelector('[data-routine-selected]');
    this.submitButton = this.querySelector('[data-routine-submit]');

    // Gift steps: block indices whose routine-group has data-is-gift="true".
    // Selections made while `currentStep` is in this set are tagged as gifts
    // and priced at 0 in the tracker.
    this.giftSteps = new Set();
    this.groups.forEach((group, i) => {
      if (group.dataset.isGift === 'true') this.giftSteps.add(i);
    });

    // Automatic gifts: server-rendered, added to cart unconditionally.
    // Their variant ids live on [data-auto-gift-variant-id] elements in the
    // review section; JS just reads them and tags each with _routineGift on
    // cart submit. They don't count toward the tracker (not user-chosen).
    this.autoGiftElements = Array.from(
      this.querySelectorAll('[data-auto-gift-variant-id]')
    );

    // Sticky tracker bar (count + price + progress + CTA).
    this.tracker = {
      root: this.querySelector('[data-routine-tracker]'),
      count: this.querySelector('[data-routine-tracker-count]'),
      priceWrap: this.querySelector('[data-routine-tracker-price]'),
      priceSale: this.querySelector('[data-routine-tracker-price-sale]'),
      priceOriginal: this.querySelector('[data-routine-tracker-price-original]'),
      progress: this.querySelector('[data-routine-tracker-progress]'),
      progressFill: this.querySelector('[data-routine-tracker-progress-fill]'),
      cta: this.querySelector('[data-routine-tracker-cta]'),
    };
    // Read both templates from window.routine_language — the CTA's initial
    // textContent is no good because Liquid already substituted {{ count }}
    // server-side, leaving no placeholder for JS to replace.
    const lang = window.routine_language || {};
    this.ctaTemplates = {
      pickMore: lang.pick_more || 'pick {{ count }} more',
      addToCart: lang.add_to_cart || 'add to cart',
    };

    // Currency formatter for the tracker totals.
    this.currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    this.locale = document.documentElement.lang || undefined;

    this.bindEvents();
    this.updateNavState();
    this.updateTracker();
  }

  bindEvents() {
    // Single-variant "add to routine" buttons inside selection steps.
    this.querySelectorAll('[data-routine-select]').forEach((btn) => {
      btn.addEventListener('click', this.onSingleVariantClick.bind(this));
    });

    // Navigation items (let user jump back to a completed step).
    this.navigationItems.forEach((item) => {
      item.addEventListener('click', this.onNavClick.bind(this));
    });

    // Back buttons (one per middle step).
    this.querySelectorAll('.routine-header__back').forEach((btn) => {
      btn.addEventListener('click', this.goBack.bind(this));
    });

    // Reset buttons (live on the review step).
    this.querySelectorAll('.routine-header__reset').forEach((btn) => {
      btn.addEventListener('click', this.reset.bind(this));
    });

    // Add-to-cart on review step.
    if (this.submitButton) {
      this.submitButton.addEventListener('click', this.addToCart.bind(this));
    }

    // Variant selection from the quickview modal.
    subscribe(BYOR_EVENTS.productSelected, this.onProductSelected.bind(this));
  }

  /* ------------------------------------------------------------------ */
  /* Selection handlers                                                  */
  /* ------------------------------------------------------------------ */

  onSingleVariantClick(e) {
    const btn = e.currentTarget;
    if (btn.disabled) return;

    this.selectProduct({
      variantId: btn.dataset.variantId,
      variantTitle: btn.dataset.variantTitle || '',
      price: parseInt(btn.dataset.variantPrice, 10) || 0,
      productId: btn.dataset.productId,
      handle: btn.dataset.productHandle,
      title: btn.dataset.productTitle,
      image: btn.dataset.productImage,
    });
  }

  onProductSelected({ data }) {
    if (!data) return;
    this.selectProduct(data);
  }

  selectProduct(product) {
    if (!product || !product.variantId) return;
    if (this.currentStep >= this.selectionCount) return;

    // A step marked `is_gift` overrides whatever the button/modal sent:
    // the entry is always tagged as a gift and priced at 0.
    const isGiftStep = this.giftSteps.has(this.currentStep);
    const entry = isGiftStep
      ? { ...product, isGift: true, price: 0 }
      : product;

    this.bundle[this.currentStep] = entry;
    this.updateNavigationThumbnail(this.currentStep, entry);
    this.updateTracker();
    this.goToNextEmpty();
  }

  /* ------------------------------------------------------------------ */
  /* Navigation                                                          */
  /* ------------------------------------------------------------------ */

  onNavClick(e) {
    const item = e.currentTarget;
    const target = parseInt(item.dataset.index, 10);
    if (Number.isNaN(target)) return;

    // Only allow jumping to steps the user has already reached.
    // The current step is always reachable, and any prior step is reachable.
    // The review step is reachable only when every selection is filled.
    if (target > this.currentStep) {
      if (target === this.selectionCount && this.isComplete()) {
        // ok — jump forward to review
      } else {
        return;
      }
    }

    this.goToStep(target);
  }

  goBack() {
    this.goToStep(Math.max(0, this.currentStep - 1));
  }

  goToNextEmpty() {
    // BYOB-style: after a selection, jump to the first still-empty slot.
    // If everything is filled, jump to the review step.
    const next = this.bundle.findIndex((item) => item == null);
    if (next === -1) {
      this.goToStep(this.selectionCount); // review
    } else {
      this.goToStep(next);
    }
  }

  goToStep(index) {
    const clamped = Math.max(0, Math.min(index, this.groups.length - 1));
    this.currentStep = clamped;
    this.dataset.currentStep = clamped;

    this.groups.forEach((group, i) => {
      group.classList.toggle('active', i === clamped);
    });

    this.updateNavState();

    if (this.reviewGroup && this.groups[clamped] === this.reviewGroup) {
      this.renderReview();
    }

    this.scrollToTop();
    this.focusStepHeading(clamped);
  }

  focusStepHeading(index) {
    const group = this.groups[index];
    if (!group) return;
    const heading = group.querySelector('.routine-header__heading');
    if (!heading) return;
    if (!heading.hasAttribute('tabindex')) {
      heading.setAttribute('tabindex', '-1');
    }
    // preventScroll: our own scrollToTop is already doing the scroll work.
    try {
      heading.focus({ preventScroll: true });
    } catch (e) {
      heading.focus();
    }
  }

  updateNavState() {
    this.navigationItems.forEach((item, i) => {
      const isActive = i === this.currentStep;
      item.classList.toggle('active', isActive);
      if (isActive) {
        item.setAttribute('aria-current', 'step');
      } else {
        item.removeAttribute('aria-current');
      }
      // Mark items as "completed" so CSS can style visited ones differently.
      const filled = i < this.selectionCount && this.bundle[i] != null;
      item.classList.toggle('completed', filled);
    });
  }

  updateNavigationThumbnail(index, product) {
    const item = this.navigationItems[index];
    if (!item || !product.image) return;
    item.style.setProperty('--routine-nav-image', `url('${product.image}')`);
    item.classList.add('selected');
  }

  clearNavigationThumbnail(index) {
    const item = this.navigationItems[index];
    if (!item) return;
    item.style.removeProperty('--routine-nav-image');
    item.classList.remove('selected');
  }

  /* ------------------------------------------------------------------ */
  /* Review + submit                                                     */
  /* ------------------------------------------------------------------ */

  isComplete() {
    return (
      this.bundle.length === this.selectionCount &&
      this.bundle.every((item) => item != null)
    );
  }

  renderReview() {
    if (!this.selectedContainer) return;

    this.selectedContainer.innerHTML = '';

    this.bundle.forEach((product) => {
      if (!product) return;
      const item = document.createElement('div');
      const giftClass = product.isGift ? ' routine-product--gift' : '';
      item.className = `routine-product routine-product--selected${giftClass}`;
      const subtitle = product.isGift
        ? this.escape(
            (window.routine_language && window.routine_language.free_gift) ||
              'free gift'
          )
        : product.variantTitle && product.variantTitle !== 'Default Title'
        ? this.escape(product.variantTitle)
        : '';
      item.innerHTML = `
        <div class="routine-product__image">
          <div class="media media--square">
            ${product.image ? `<img src="${product.image}" alt="${this.escape(product.title)}" loading="lazy">` : ''}
          </div>
        </div>
        <div class="routine-product__details">
          <div class="routine-product__title">${this.escape(product.title || '')}</div>
          ${subtitle ? `<div class="routine-product__description">${subtitle}</div>` : ''}
        </div>
      `;
      this.selectedContainer.appendChild(item);
    });

    if (this.submitButton) {
      this.submitButton.disabled = !this.isComplete();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Sticky tracker bar                                                  */
  /* ------------------------------------------------------------------ */

  updateTracker() {
    if (!this.tracker || !this.tracker.root) return;

    const filledCount = this.bundle.filter((item) => item != null).length;
    const complete = this.isComplete();

    // "X of N picked" counter
    if (this.tracker.count) {
      this.tracker.count.textContent = String(filledCount);
    }

    // Progress bar fill — % of selection steps completed.
    const percent =
      this.selectionCount > 0 ? (filledCount / this.selectionCount) * 100 : 0;
    if (this.tracker.progressFill) {
      this.tracker.progressFill.style.width = `${percent}%`;
    }
    if (this.tracker.progress) {
      this.tracker.progress.setAttribute('aria-valuenow', String(filledCount));
    }

    // Subtotal: sum the chosen variants' prices (cents),
    // then apply the same 30% discount the product cards show.
    const originalCents = this.bundle.reduce((sum, item) => {
      if (!item || !item.price) return sum;
      return sum + item.price;
    }, 0);
    const discountedCents = Math.round(originalCents * BYOR_DISCOUNT_MULTIPLIER);

    if (this.tracker.priceWrap) {
      if (filledCount === 0) {
        this.tracker.priceWrap.hidden = true;
      } else {
        this.tracker.priceWrap.hidden = false;
        if (this.tracker.priceSale) {
          this.tracker.priceSale.textContent = this.formatPrice(discountedCents);
        }
        if (this.tracker.priceOriginal) {
          this.tracker.priceOriginal.textContent = this.formatPrice(originalCents);
          this.tracker.priceOriginal.hidden = originalCents === discountedCents;
        }
      }
    }

    // CTA: disabled + "pick X more" until full, then "add to cart".
    if (this.tracker.cta) {
      this.tracker.cta.disabled = !complete;
      if (complete) {
        this.tracker.cta.textContent = this.ctaTemplates.addToCart;
      } else {
        const remaining = this.selectionCount - filledCount;
        this.tracker.cta.textContent = this.ctaTemplates.pickMore.replace(
          '{{ count }}',
          String(remaining)
        );
      }
    }
  }

  formatPrice(cents) {
    if (!Number.isFinite(cents)) return '';
    const dollars = cents / 100;
    const hasCents = cents % 100 !== 0;
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(dollars);
    } catch (e) {
      return `$${dollars.toFixed(hasCents ? 2 : 0)}`;
    }
  }

  addToCart() {
    if (!this.submitButton || this.submitButton.disabled) return;
    if (!this.isComplete()) return;

    this.submitButton.disabled = true;
    this.submitButton.classList.add('loading');

    const gid = Math.floor(Math.random() * 1_000_000_000);

    const autoGiftVariantIds = this.autoGiftElements
      .map((el) => parseInt(el.dataset.autoGiftVariantId, 10))
      .filter((id) => !Number.isNaN(id));

    const totalSize = this.bundle.length + autoGiftVariantIds.length;

    const items = [];

    this.bundle.forEach((product) => {
      if (!product || !product.variantId) return;
      const properties = {
        _gid: gid,
        _routineType: this.routineType,
        _routineSize: totalSize,
      };
      if (product.isGift) {
        properties._routineGift = 'TRUE';
      }
      items.push({
        id: parseInt(product.variantId, 10),
        quantity: 1,
        properties,
      });
    });

    autoGiftVariantIds.forEach((variantId) => {
      items.push({
        id: variantId,
        quantity: 1,
        properties: {
          _gid: gid,
          _routineType: this.routineType,
          _routineSize: totalSize,
          _routineGift: 'TRUE',
        },
      });
    });

    const cartAddUrl =
      (typeof routes !== 'undefined' && routes.cart_add_url) || '/cart/add.js';
    const cartUrl =
      (typeof routes !== 'undefined' && routes.cart_url) || '/cart';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw err;
          });
        }
        return response.json();
      })
      .then(() => {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.fullUpdate === 'function') {
          return cartDrawer.fullUpdate().then(() => cartDrawer.open());
        }
        window.location.href = cartUrl;
      })
      .catch((err) => {
        console.error('[byor] add to cart failed', err);
      })
      .finally(() => {
        // Re-enable so the user can add the same routine again — addToCart()
        // generates a fresh `_gid` on each call, so the next click produces a
        // new grouped bundle in the cart.
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('loading');
      });
  }

  /* ------------------------------------------------------------------ */
  /* Reset                                                               */
  /* ------------------------------------------------------------------ */

  reset() {
    this.bundle = new Array(this.selectionCount).fill(null);
    this.bundle.forEach((_, i) => this.clearNavigationThumbnail(i));
    if (this.selectedContainer) this.selectedContainer.innerHTML = '';
    this.updateTracker();
    this.goToStep(0);
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  scrollToTop() {
    const reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rect = this.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const targetPosition = rect.top + scrollTop - 60;
    window.scrollTo({
      top: targetPosition,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  escape(str) {
    const div = document.createElement('div');
    div.textContent = String(str);
    return div.innerHTML;
  }
}

if (!customElements.get('build-your-routine')) {
  customElements.define('build-your-routine', BuildYourRoutine);
}

/* ==================================================================== */
/* Quickview trigger: opens the byor_quickview modal for a product       */
/* ==================================================================== */

class RoutineQuickviewTrigger extends HTMLElement {
  constructor() {
    super();

    this.productUrl = (this.dataset.productUrl || '').split('?')[0];
    this.button = this.querySelector('button');

    if (this.button) {
      this.button.addEventListener('click', this.onButtonClick.bind(this));
    }
  }

  onButtonClick(e) {
    e.preventDefault();

    fetch(`${this.productUrl}?section_id=byor_quickview`)
      .then((response) => response.text())
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');

        const oldModal = document.querySelector('#ByorQuickviewModal');
        if (oldModal) oldModal.remove();

        const modal = responseHTML.querySelector('#ByorQuickviewModal');
        if (!modal) return;

        document.body.appendChild(modal);
        modal.show(this.button);
      });
  }
}

if (!customElements.get('routine-quickview-trigger')) {
  customElements.define('routine-quickview-trigger', RoutineQuickviewTrigger);
}

/* ==================================================================== */
/* Quickview modal contents                                              */
/* ==================================================================== */

class RoutineQuickview extends HTMLElement {
  constructor() {
    super();

    this.productUrl = this.dataset.productUrl;

    // Re-fetch the modal contents when the user changes a variant option.
    subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this)
    );

    // The submit button lives inside .quickview-container__info, which we
    // swap out on every variant change — use delegation so the listener
    // survives re-renders.
    this.addEventListener('click', this.onClick.bind(this));
  }

  onClick(e) {
    const submit = e.target.closest('[data-routine-quickview-submit]');
    if (!submit) return;
    if (submit.disabled) return;

    e.preventDefault();

    const product = this.getSelectedProduct(submit);
    if (!product) return;

    publish(BYOR_EVENTS.productSelected, { data: product });

    const modal = this.closest('modal-dialog');
    if (modal) modal.hide();
  }

  getSelectedProduct(submitButton) {
    // Prefer the live JSON the variant-selects component keeps in sync.
    const variantScript = this.querySelector(
      'variant-selects [data-selected-variant]'
    );
    let variant = null;
    if (variantScript) {
      try {
        variant = JSON.parse(variantScript.textContent);
      } catch (e) {
        variant = null;
      }
    }

    const variantId =
      (variant && variant.id) ||
      submitButton.dataset.variantId ||
      null;

    if (!variantId) return null;

    // Image: prefer the variant's featured image, fall back to whatever's
    // visible in the modal.
    let image =
      (variant && variant.featured_image && variant.featured_image.src) ||
      null;
    if (!image) {
      const img = this.querySelector('.quickview-container__image img');
      if (img) image = img.currentSrc || img.src;
    }

    const titleEl = this.querySelector('.product__title h1, .product__title');
    const title =
      submitButton.dataset.productTitle ||
      (titleEl && titleEl.textContent.trim()) ||
      '';

    const variantTitle = (variant && variant.title) || '';
    const price = (variant && Number.isFinite(variant.price)) ? variant.price : 0;

    return {
      variantId: String(variantId),
      variantTitle,
      price,
      productId: submitButton.dataset.productId || '',
      handle: submitButton.dataset.productHandle || '',
      title,
      image,
    };
  }

  handleOptionValueChange({ data: { selectedOptionValues } }) {
    const productUrl = this.buildRequestUrlWithParams(selectedOptionValues);

    fetch(`${productUrl}`)
      .then((response) => response.text())
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');

        const quickviewContainer = responseHTML.querySelector('#ByorQuickbuyContainer');
        if (!quickviewContainer) return;

        const imageContainer = quickviewContainer.querySelector('.quickview-container__image');
        const infoContainer = quickviewContainer.querySelector('.quickview-container__info');

        if (imageContainer) {
          this.querySelector('.quickview-container__image').innerHTML = imageContainer.innerHTML;
        }
        if (infoContainer) {
          this.querySelector('.quickview-container__info').innerHTML = infoContainer.innerHTML;
        }
      });
  }

  buildRequestUrlWithParams(optionValues) {
    const params = [`section_id=byor_quickview`];
    if (optionValues && optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }
    return `${this.productUrl}?${params.join('&')}`;
  }
}

if (!customElements.get('routine-quickview')) {
  customElements.define('routine-quickview', RoutineQuickview);
}
